import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Avatar } from './layout';
import { Icon, Logo, type IconName } from './icons';
import { NetworkBanner } from './states';
import { useModalLayer } from './panels';

export type NavItem = {
  to: string; label: string; icon: IconName; badge?: number | string | null; badgeLabel?: string; end?: boolean;
  /** Further paths that belong to this section (e.g. /einheiten under Objekte); a `*` segment matches any one
   * segment, so the meters of every property can belong to Zähler. The most specific match wins (one active item). */
  also?: string[];
};

/** Number of matched segments when `pathname` is `pattern` or below it (`*` = any one segment), else -1. */
function depth(pathname: string, pattern: string, end = false): number {
  const want = pattern.split('/').filter(Boolean);
  const have = pathname.split('/').filter(Boolean);
  if (have.length < want.length || (end && have.length !== want.length)) return -1;
  return want.every((seg, k) => seg === '*' || seg === have[k]) ? want.length : -1;
}

/** Active item of a navigation: the one whose own path or `also` pattern matches the location most specifically. */
export function useNavActive(items: NavItem[]) {
  const { pathname } = useLocation();
  const score = (i: NavItem) => Math.max(depth(pathname, i.to, i.end), ...(i.also ?? []).map((p) => depth(pathname, p)));
  const scores = items.map(score);
  const best = Math.max(-1, ...scores);
  const winner = best < 0 ? null : items[scores.indexOf(best)]!;
  return (i: NavItem) => i === winner;
}

/** Link of a navigation item; `aria-current` follows the computed active item (not NavLink's own matching). */
function NavItemLink({ item, active, children }: { item: NavItem; active: boolean; children: ReactNode }) {
  return <Link to={item.to} className={active ? 'active' : undefined} aria-current={active ? 'page' : undefined}>{children}</Link>;
}

/** Level of the landlord frame (design/NavKonzept): "Alle Objekte" (dark sidebar) or inside one property (light). */
export type ShellLevel = 'portfolio' | 'objekt';
/** Sidebar as a drawer on small screens: opened from the top bar, closed by Escape, the scrim or the close button. */
export type ShellDrawer = { open: boolean; onClose: () => void; label: string };

const SMALL = '(max-width: 900px)';
const isSmall = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(SMALL).matches;

/**
 * App frame: skip link, sidebar, main content (focus target), optional mobile tab bar. With `level` the frame shows
 * which navigation level is active (light sidebar and a petrol band above the page inside a property); `topBar` is the
 * bar above the page on small screens and `drawer` turns the sidebar into a drawer there.
 */
/**
 * `aside`: a panel on the right (KI-Assistent), a third grid column while `asideOpen` on wide screens; on small screens
 * the app renders it as an overlay sheet itself.
 */
export function AppShell({ sidebar, children, mobileNav, after, level, topBar, bar, drawer, aside, asideOpen }: { sidebar: ReactNode; children: ReactNode; mobileNav?: NavItem[]; after?: ReactNode; level?: ShellLevel; topBar?: ReactNode; /** Slim bar at the top of the page area on wide screens (e.g. "+ Beleg"); small screens use `topBar`. */ bar?: ReactNode; drawer?: ShellDrawer; aside?: ReactNode; asideOpen?: boolean }) {
  const { t } = useTranslation('common');
  const isActive = useNavActive(mobileNav ?? []);
  const panel = useRef<HTMLDivElement>(null);
  const open = !!drawer?.open;
  const onClose = drawer?.onClose;
  const modal = open && isSmall();
  useModalLayer(modal, panel, () => onClose?.());
  useEffect(() => {
    // growing past the breakpoint shows the regular sidebar: the drawer state ends there
    if (!open || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(SMALL);
    const onChange = () => { if (!mq.matches) onClose?.(); };
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [open, onClose]);
  return (
    <div className="shell" data-level={level} data-aside={aside && asideOpen ? 'open' : undefined}>
      <a className="skip-link" href="#main">{t('ui.skipToContent')}</a>
      {topBar && <div className="shell-top">{topBar}</div>}
      <div className={`shell-side${open ? ' open' : ''}`} onMouseDown={(e) => { if (open && e.target === e.currentTarget) onClose?.(); }}>
        <div className="shell-panel" ref={panel} tabIndex={-1} role={modal ? 'dialog' : undefined} aria-modal={modal || undefined} aria-label={modal ? drawer?.label : undefined}>
          {drawer && (
            <div className="drawer-head">
              <button type="button" className="drawer-close" onClick={drawer.onClose} data-autofocus={open || undefined}>
                <Icon name="close" size={18} />
                {t('close')}
              </button>
            </div>
          )}
          {sidebar}
        </div>
      </div>
      <main className="main" id="main" tabIndex={-1}>
        <NetworkBanner />
        {bar && <div className="shell-bar">{bar}</div>}
        {children}
      </main>
      {aside}
      {after}
      {mobileNav && (
        <nav className="mobile-nav" aria-label={t('ui.nav.mobile')}>
          {mobileNav.map((i) => (
            <NavItemLink key={i.to} item={i} active={isActive(i)}>
              <Icon name={i.icon} size={20} />
              {i.label}
            </NavItemLink>
          ))}
        </nav>
      )}
    </div>
  );
}

/** A titled group of sidebar items (e.g. the sections of the selected property, then the account-wide ones). */
export type NavSection = { key: string; label?: string; items: NavItem[] };

/** Items of a sidebar group: icon, label, amber count badge (with a spoken text); `isActive` from useNavActive. */
export function NavList({ items, isActive }: { items: NavItem[]; isActive: (i: NavItem) => boolean }) {
  return (
    <ul className="nav-list">
      {items.map((i) => (
        <li key={i.to}>
          <NavItemLink item={i} active={isActive(i)}>
            <span className="nav-label">
              <Icon name={i.icon} size={18} />
              {i.label}
            </span>
            {i.badge ? (
              <span className="nav-badge">
                <span aria-hidden>{i.badge}</span>
                {i.badgeLabel && <span className="sr-only">{i.badgeLabel}</span>}
              </span>
            ) : null}
          </NavItemLink>
        </li>
      ))}
    </ul>
  );
}

/**
 * Sidebar (design/Sidebar.dc.html): brand, nav items with icon + amber count badge, footer slot. `sections` (instead of
 * `items`) groups the items under small headings; one item is active across all sections (the most specific match).
 */
export function Sidebar({ items = [], sections, label, brand, brandTo = '/', top, footer }: { items?: NavItem[]; sections?: NavSection[]; label: string; brand: ReactNode; brandTo?: string; top?: ReactNode; footer?: ReactNode }) {
  const groups: NavSection[] = sections ?? [{ key: 'main', items }];
  const isActive = useNavActive(groups.flatMap((g) => g.items));
  return (
    <nav className="sidebar" aria-label={label}>
      <Link to={brandTo} className="brand">
        <Logo size={28} />
        {brand}
      </Link>
      {top}
      {groups.map((g) => (g.label ? (
        <div key={g.key} className="nav-section" role="group" aria-label={g.label}>
          <span className="nav-section-title" aria-hidden>{g.label}</span>
          <NavList items={g.items} isActive={isActive} />
        </div>
      ) : <NavList key={g.key} items={g.items} isActive={isActive} />))}
      <div className="spacer" />
      {footer && <div className="sidebar-foot">{footer}</div>}
    </nav>
  );
}

/**
 * Horizontal section links for small screens (the sidebar is hidden there): a scrollable row of chips with the same
 * active logic as the sidebar. `top` holds e.g. the property switcher.
 */
export function ScopeBar({ label, items, top }: { label: string; items: NavItem[]; top?: ReactNode }) {
  const isActive = useNavActive(items);
  return (
    <nav className="scope-bar" aria-label={label}>
      {top}
      <ul className="scope-links">
        {items.map((i) => (
          <li key={i.to}>
            <NavItemLink item={i} active={isActive(i)}>
              {i.label}
              {i.badge ? <span className="nav-badge"><span aria-hidden>{i.badge}</span>{i.badgeLabel && <span className="sr-only">{i.badgeLabel}</span>}</span> : null}
            </NavItemLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** Account block at the bottom of the sidebar; opens a small menu (links, logout). */
export function AccountMenu({ name, avatarName, sub, email, label, children }: { name: string; /** Person behind the account (initials); defaults to `name`. */ avatarName?: string; sub?: ReactNode; email?: string; label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return (
    <div ref={box} onClick={(e) => { if ((e.target as HTMLElement).closest('.account-menu a, .account-menu button')) setOpen(false); }}>
      {open && (
        <div className="account-menu" id={id} role="group" aria-label={label}>
          {email && <div className="menu-head">{email}</div>}
          {children}
        </div>
      )}
      <button type="button" className="account-button" aria-expanded={open} aria-controls={open ? id : undefined} onClick={() => setOpen((o) => !o)}>
        <Avatar name={avatarName ?? name} />
        <span className="who">
          <strong className="truncate">{name}</strong>
          {sub && <span className="truncate">{sub}</span>}
        </span>
      </button>
    </div>
  );
}
